import { supabase } from '../lib/supabase';

/**
 * Job Opportunities Service
 * Handles CRUD operations for jobs, applications, and related functionality
 */

/**
 * Get all active job opportunities with pagination
 * @param {Object} options - Query options
 * @returns {Promise<Object>} Jobs data with pagination info
 */
export async function getJobOpportunities(options = {}) {
  try {
    const {
      page = 1,
      limit = 10,
      category = null,
      jobType = null,
      experienceLevel = null,
      location = null,
      isRemote = null,
      searchQuery = null,
    } = options;

    let query = supabase
      ?.from('job_opportunities')
      ?.select(`
        id,
        title,
        description,
        job_type,
        category,
        experience_level,
        location,
        is_remote,
        salary_min,
        salary_max,
        currency,
        requirements,
        skills_required,
        application_deadline,
        status,
        application_count,
        view_count,
        is_featured,
        created_at,
        user_profiles!posted_by(
          id,
          full_name,
          organization,
          profile_image_url
        )
      `)
      ?.eq('status', 'active');

    // Apply filters
    if (category) {
      query = query?.eq('category', category);
    }
    if (jobType) {
      query = query?.eq('job_type', jobType);
    }
    if (experienceLevel) {
      query = query?.eq('experience_level', experienceLevel);
    }
    if (location) {
      query = query?.ilike('location', `%${location}%`);
    }
    if (isRemote !== null) {
      query = query?.eq('is_remote', isRemote);
    }
    if (searchQuery) {
      query = query?.or(`title.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%`);
    }

    // Add pagination
    const offset = (page - 1) * limit;
    query = query
      ?.range(offset, offset + limit - 1)
      ?.order('created_at', { ascending: false });

    const { data: jobs, error, count } = await query;

    if (error) throw error;

    return {
      jobs: jobs || [],
      totalCount: count || 0,
      page,
      limit,
      hasMore: (count || 0) > offset + limit,
    };
  } catch (error) {
    console.error('Error fetching job opportunities:', error);
    throw error;
  }
}

/**
 * Get job opportunity by ID
 * @param {string} jobId - Job ID
 * @returns {Promise<Object>} Job details
 */
export async function getJobById(jobId) {
  try {
    const { data: job, error } = await supabase
      ?.from('job_opportunities')
      ?.select(`
        *,
        user_profiles!posted_by(
          id,
          full_name,
          organization,
          profile_image_url,
          title,
          bio
        )
      `)
      ?.eq('id', jobId)
      ?.single();

    if (error) throw error;

    // Increment view count
    await supabase
      ?.from('job_opportunities')
      ?.update({ view_count: (job?.view_count || 0) + 1 })
      ?.eq('id', jobId);

    return job;
  } catch (error) {
    console.error('Error fetching job details:', error);
    throw error;
  }
}

/**
 * Create new job opportunity
 * @param {Object} jobData - Job data
 * @returns {Promise<Object>} Created job
 */
export async function createJobOpportunity(jobData) {
  try {
    const { data: job, error } = await supabase
      ?.from('job_opportunities')
      ?.insert({
        ...jobData,
        posted_by: jobData?.userId || (await supabase?.auth?.getUser())?.data?.user?.id,
      })
      ?.select()
      ?.single();

    if (error) throw error;
    return job;
  } catch (error) {
    console.error('Error creating job opportunity:', error);
    throw error;
  }
}

/**
 * Update job opportunity
 * @param {string} jobId - Job ID
 * @param {Object} updates - Update data
 * @returns {Promise<Object>} Updated job
 */
export async function updateJobOpportunity(jobId, updates) {
  try {
    const { data: job, error } = await supabase
      ?.from('job_opportunities')
      ?.update({
        ...updates,
        updated_at: new Date()?.toISOString(),
      })
      ?.eq('id', jobId)
      ?.select()
      ?.single();

    if (error) throw error;
    return job;
  } catch (error) {
    console.error('Error updating job opportunity:', error);
    throw error;
  }
}

/**
 * Delete job opportunity
 * @param {string} jobId - Job ID
 * @returns {Promise<boolean>} Success status
 */
export async function deleteJobOpportunity(jobId) {
  try {
    const { error } = await supabase
      ?.from('job_opportunities')
      ?.delete()
      ?.eq('id', jobId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting job opportunity:', error);
    throw error;
  }
}

/**
 * Apply for a job
 * @param {Object} applicationData - Application data
 * @returns {Promise<Object>} Created application
 */
export async function applyForJob(applicationData) {
  try {
    const currentUser = await supabase?.auth?.getUser();
    const userId = currentUser?.data?.user?.id;

    if (!userId) {
      throw new Error('User must be authenticated to apply for jobs');
    }

    const { data: application, error } = await supabase
      ?.from('job_applications')
      ?.insert({
        ...applicationData,
        applicant_id: userId,
      })
      ?.select()
      ?.single();

    if (error) throw error;
    return application;
  } catch (error) {
    console.error('Error applying for job:', error);
    throw error;
  }
}

/**
 * Get user's job applications
 * @param {string} userId - User ID (optional, defaults to current user)
 * @returns {Promise<Array>} User's applications
 */
export async function getUserApplications(userId = null) {
  try {
    const currentUser = await supabase?.auth?.getUser();
    const targetUserId = userId || currentUser?.data?.user?.id;

    if (!targetUserId) {
      throw new Error('User ID is required');
    }

    const { data: applications, error } = await supabase
      ?.from('job_applications')
      ?.select(`
        *,
        job_opportunities(
          id,
          title,
          company_name:user_profiles!posted_by(organization),
          location,
          job_type,
          status
        )
      `)
      ?.eq('applicant_id', targetUserId)
      ?.order('applied_at', { ascending: false });

    if (error) throw error;
    return applications || [];
  } catch (error) {
    console.error('Error fetching user applications:', error);
    throw error;
  }
}

/**
 * Get applications for employer's jobs
 * @param {string} employerId - Employer user ID (optional, defaults to current user)
 * @returns {Promise<Array>} Applications for employer's jobs
 */
export async function getEmployerApplications(employerId = null) {
  try {
    const currentUser = await supabase?.auth?.getUser();
    const targetEmployerId = employerId || currentUser?.data?.user?.id;

    if (!targetEmployerId) {
      throw new Error('Employer ID is required');
    }

    const { data: applications, error } = await supabase
      ?.from('job_applications')
      ?.select(`
        *,
        job_opportunities!inner(
          id,
          title,
          location,
          job_type
        ),
        applicant:user_profiles!applicant_id(
          id,
          full_name,
          email,
          profile_image_url,
          organization,
          bio
        )
      `)
      ?.eq('job_opportunities.posted_by', targetEmployerId)
      ?.order('applied_at', { ascending: false });

    if (error) throw error;
    return applications || [];
  } catch (error) {
    console.error('Error fetching employer applications:', error);
    throw error;
  }
}

/**
 * Update application status
 * @param {string} applicationId - Application ID
 * @param {string} status - New status
 * @param {string} reviewerNotes - Optional reviewer notes
 * @returns {Promise<Object>} Updated application
 */
export async function updateApplicationStatus(applicationId, status, reviewerNotes = null) {
  try {
    const updates = {
      status,
      updated_at: new Date()?.toISOString(),
    };

    if (status === 'reviewed') {
      updates.reviewed_at = new Date()?.toISOString();
    }

    if (reviewerNotes) {
      updates.reviewer_notes = reviewerNotes;
    }

    const { data: application, error } = await supabase
      ?.from('job_applications')
      ?.update(updates)
      ?.eq('id', applicationId)
      ?.select()
      ?.single();

    if (error) throw error;
    return application;
  } catch (error) {
    console.error('Error updating application status:', error);
    throw error;
  }
}

/**
 * Save/bookmark a job
 * @param {string} jobId - Job ID
 * @returns {Promise<Object>} Saved job record
 */
export async function saveJob(jobId) {
  try {
    const currentUser = await supabase?.auth?.getUser();
    const userId = currentUser?.data?.user?.id;

    if (!userId) {
      throw new Error('User must be authenticated to save jobs');
    }

    const { data: savedJob, error } = await supabase
      ?.from('saved_jobs')
      ?.insert({
        user_id: userId,
        job_id: jobId,
      })
      ?.select()
      ?.single();

    if (error) throw error;
    return savedJob;
  } catch (error) {
    console.error('Error saving job:', error);
    throw error;
  }
}

/**
 * Remove saved job
 * @param {string} jobId - Job ID
 * @returns {Promise<boolean>} Success status
 */
export async function unsaveJob(jobId) {
  try {
    const currentUser = await supabase?.auth?.getUser();
    const userId = currentUser?.data?.user?.id;

    if (!userId) {
      throw new Error('User must be authenticated');
    }

    const { error } = await supabase
      ?.from('saved_jobs')
      ?.delete()
      ?.eq('user_id', userId)
      ?.eq('job_id', jobId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error removing saved job:', error);
    throw error;
  }
}

/**
 * Get user's saved jobs
 * @param {string} userId - User ID (optional, defaults to current user)
 * @returns {Promise<Array>} Saved jobs
 */
export async function getSavedJobs(userId = null) {
  try {
    const currentUser = await supabase?.auth?.getUser();
    const targetUserId = userId || currentUser?.data?.user?.id;

    if (!targetUserId) {
      throw new Error('User ID is required');
    }

    const { data: savedJobs, error } = await supabase
      ?.from('saved_jobs')
      ?.select(`
        id,
        saved_at,
        job_opportunities(
          id,
          title,
          description,
          job_type,
          category,
          location,
          is_remote,
          salary_min,
          salary_max,
          currency,
          application_deadline,
          status,
          user_profiles!posted_by(
            organization,
            full_name
          )
        )
      `)
      ?.eq('user_id', targetUserId)
      ?.order('saved_at', { ascending: false });

    if (error) throw error;
    return savedJobs || [];
  } catch (error) {
    console.error('Error fetching saved jobs:', error);
    throw error;
  }
}

/**
 * Get job statistics for dashboard
 * @returns {Promise<Object>} Job statistics
 */
export async function getJobStatistics() {
  try {
    const [
      totalJobsResult,
      activeJobsResult,
      totalApplicationsResult,
    ] = await Promise.all([
      supabase?.from('job_opportunities')?.select('*', { count: 'exact', head: true }) || { count: 0 },
      supabase?.from('job_opportunities')?.select('*', { count: 'exact', head: true })?.eq('status', 'active') || { count: 0 },
      supabase?.from('job_applications')?.select('*', { count: 'exact', head: true }) || { count: 0 },
    ]);

    return {
      totalJobs: totalJobsResult?.count || 0,
      activeJobs: activeJobsResult?.count || 0,
      totalApplications: totalApplicationsResult?.count || 0,
    };
  } catch (error) {
    console.error('Error fetching job statistics:', error);
    return {
      totalJobs: 0,
      activeJobs: 0,
      totalApplications: 0,
    };
  }
}